import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Copy, Check, Pencil, RotateCcw, Link2, MessageSquare, Calendar, Plane, QrCode, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Configuration des moments avec leurs métadonnées
const MOMENT_CONFIG = {
  reservation: {
    key: 'reservation',
    label: 'Après la réservation',
    icon: Calendar,
    color: 'blue',
    description: 'Envoyez ce message juste après la confirmation de réservation'
  },
  pre_arrivee: {
    key: 'pre_arrivee',
    label: 'Avant l\'arrivée',
    icon: Plane,
    color: 'green',
    description: 'Envoyez ce message quelques jours avant l\'arrivée du voyageur'
  },
  avant_depart: {
    key: 'avant_depart',
    label: 'Avant le départ',
    icon: Home,
    color: 'orange',
    description: 'Envoyez ce message la veille ou le jour du départ'
  },
  qr_code: {
    key: 'qr_code',
    label: 'QR Code dans le logement',
    icon: QrCode,
    color: 'purple',
    description: 'Imprimez ce lien sous forme de QR code à placer dans le logement'
  }
};

// Messages par défaut (templates sans nom ni lien - générés dynamiquement)
const DEFAULT_MESSAGES = {
  reservation: `Bonjour 👋
Merci pour votre réservation à {NOM_LOGEMENT}.
Pour préparer au mieux votre séjour, vous pouvez dès maintenant découvrir les options et services disponibles pour ce logement :
{LIEN}
À bientôt !`,
  pre_arrivee: `Bonjour 👋
Votre arrivée à {NOM_LOGEMENT} approche !
Vous pouvez encore ajouter des options à votre séjour afin que tout soit prêt lors de votre arrivée :
{LIEN}
Nous vous souhaitons un excellent séjour.`,
  avant_depart: `Bonjour 👋
Nous espérons que votre séjour à {NOM_LOGEMENT} se passe bien.
Vous souhaitez profiter un peu plus longtemps de votre dernière journée ou organiser plus facilement votre départ ? Découvrez les options encore disponibles :
{LIEN}
Les options restent soumises aux disponibilités du logement.`,
  qr_code: `Bienvenue à {NOM_LOGEMENT} 👋
Besoin d'un petit plus pendant votre séjour ?
Découvrez les options et services disponibles ici :
{LIEN}`
};

const PropertyMessages = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMoment, setEditingMoment] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [copiedStates, setCopiedStates] = useState({});

  // Domaine dynamique (pas de hardcoding)
  const baseUrl = useMemo(() => {
    return window.location.origin;
  }, []);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du logement');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  // Générer le lien complet pour un moment donné
  const generateLink = useCallback((momentKey) => {
    if (!property) return '';
    return `${baseUrl}/p/${property.lien_unique}?moment=${momentKey}`;
  }, [property, baseUrl]);

  // Obtenir le message (personnalisé ou par défaut) pour un moment
  const getMessage = useCallback((momentKey) => {
    if (!property) return '';
    const customMessages = property.custom_messages || {};
    return customMessages[momentKey] || DEFAULT_MESSAGES[momentKey];
  }, [property]);

  // Générer le message complet avec nom et lien
  const getFullMessage = useCallback((momentKey) => {
    if (!property) return '';
    const template = getMessage(momentKey);
    const link = generateLink(momentKey);
    return template
      .replace(/{NOM_LOGEMENT}/g, property.nom)
      .replace(/{LIEN}/g, link);
  }, [property, getMessage, generateLink]);

  // Vérifier si un message est personnalisé
  const isCustomized = useCallback((momentKey) => {
    if (!property) return false;
    const customMessages = property.custom_messages || {};
    return customMessages[momentKey] && customMessages[momentKey] !== DEFAULT_MESSAGES[momentKey];
  }, [property]);

  // Copier dans le presse-papiers avec feedback visuel
  const copyToClipboard = async (text, type, momentKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [`${momentKey}-${type}`]: true }));
      toast.success(type === 'link' ? 'Lien copié !' : 'Message complet copié !');
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [`${momentKey}-${type}`]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Ouvrir le dialog d'édition
  const openEditDialog = (momentKey) => {
    setEditingMoment(momentKey);
    setEditingText(getMessage(momentKey));
    setEditDialogOpen(true);
  };

  // Sauvegarder le message personnalisé
  const saveMessage = async () => {
    if (!editingMoment) return;
    
    setSaving(true);
    try {
      const currentMessages = property.custom_messages || {};
      const updatedMessages = {
        ...currentMessages,
        [editingMoment]: editingText
      };
      
      await api.put(`/properties/${id}`, { custom_messages: updatedMessages });
      setProperty(prev => ({ ...prev, custom_messages: updatedMessages }));
      toast.success('Message personnalisé sauvegardé !');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser un message au texte par défaut
  const resetMessage = async (momentKey) => {
    if (!window.confirm('Réinitialiser ce message au texte par défaut ?')) return;
    
    setSaving(true);
    try {
      const currentMessages = property.custom_messages || {};
      const updatedMessages = { ...currentMessages };
      delete updatedMessages[momentKey];
      
      // Si plus aucun message personnalisé, on met null
      const finalMessages = Object.keys(updatedMessages).length > 0 ? updatedMessages : null;
      
      await api.put(`/properties/${id}`, { custom_messages: finalMessages });
      setProperty(prev => ({ ...prev, custom_messages: finalMessages }));
      toast.success('Message réinitialisé');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser dans le dialog d'édition
  const resetInDialog = () => {
    if (editingMoment) {
      setEditingText(DEFAULT_MESSAGES[editingMoment]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700'
  };

  const iconColorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => navigate('/properties')}
          data-testid="back-to-properties-btn"
          className="mb-4 sm:mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour aux logements
        </Button>

        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Liens et messages voyageurs
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            {property.nom}
          </p>
        </div>

        {/* Info box */}
        <Card className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6" data-testid="info-box">
          <p className="text-sm text-blue-800">
            <strong>Comment utiliser ces liens ?</strong><br />
            Copiez le message correspondant au moment souhaité et collez-le dans votre messagerie Airbnb, Booking ou votre PMS. 
            Les liens contiennent un paramètre Analytics qui vous permettra de suivre les performances de chaque message.
          </p>
        </Card>

        {/* Messages cards */}
        <div className="space-y-4 sm:space-y-6">
          {Object.values(MOMENT_CONFIG).map((moment) => {
            const IconComponent = moment.icon;
            const link = generateLink(moment.key);
            const fullMessage = getFullMessage(moment.key);
            const customized = isCustomized(moment.key);
            const isCopiedLink = copiedStates[`${moment.key}-link`];
            const isCopiedMessage = copiedStates[`${moment.key}-message`];

            return (
              <Card 
                key={moment.key} 
                className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden"
                data-testid={`message-card-${moment.key}`}
              >
                {/* Header */}
                <div className={`p-3 sm:p-4 border-b ${colorClasses[moment.color]}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <IconComponent className={`w-5 h-5 ${iconColorClasses[moment.color]}`} strokeWidth={1.5} />
                      <div>
                        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {moment.label}
                        </h3>
                        <p className="text-xs text-slate-600 hidden sm:block">{moment.description}</p>
                      </div>
                    </div>
                    {customized && (
                      <span className="text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded-full">
                        Personnalisé
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-4">
                  {/* Lien */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center mb-2">
                      <Link2 className="w-3 h-3 mr-1" />
                      Lien Analytics
                    </label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-xs sm:text-sm bg-slate-50 p-2 sm:p-3 rounded-lg text-slate-700 overflow-x-auto whitespace-nowrap">
                        {link}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(link, 'link', moment.key)}
                        data-testid={`copy-link-${moment.key}`}
                        className="flex-shrink-0 text-slate-600 hover:text-slate-900"
                      >
                        {isCopiedLink ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">Copier</span>
                      </Button>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center mb-2">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Message complet
                    </label>
                    <div className="bg-slate-50 p-3 sm:p-4 rounded-lg text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {fullMessage}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => copyToClipboard(fullMessage, 'message', moment.key)}
                      data-testid={`copy-message-${moment.key}`}
                      className="flex-1 sm:flex-none bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      {isCopiedMessage ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copier le message
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(moment.key)}
                      data-testid={`edit-message-${moment.key}`}
                      className="flex-1 sm:flex-none text-slate-600 hover:text-slate-900"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    {customized && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resetMessage(moment.key)}
                        data-testid={`reset-message-${moment.key}`}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Réinitialiser
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Modifier le message - {editingMoment && MOMENT_CONFIG[editingMoment]?.label}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Instructions */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Variables disponibles :</strong>
                <ul className="mt-1 ml-4 list-disc">
                  <li><code className="bg-amber-100 px-1 rounded">{'{NOM_LOGEMENT}'}</code> - Nom du logement</li>
                  <li><code className="bg-amber-100 px-1 rounded">{'{LIEN}'}</code> - Lien Analytics (généré automatiquement)</li>
                </ul>
              </div>

              {/* Textarea */}
              <Textarea
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                data-testid="edit-message-textarea"
                placeholder="Saisissez votre message personnalisé..."
              />

              {/* Preview */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
                  Aperçu
                </label>
                <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {editingText
                    .replace(/{NOM_LOGEMENT}/g, property.nom)
                    .replace(/{LIEN}/g, editingMoment ? generateLink(editingMoment) : '')}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={resetInDialog}
                  className="text-slate-600"
                  data-testid="reset-in-dialog-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Texte par défaut
                </Button>
                <div className="flex-1" />
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  onClick={saveMessage}
                  disabled={saving}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="save-message-btn"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PropertyMessages;
